import i18n from '@/i18n';
/**
 * 刊登图转 Temu 平台地址：下载原图 → 按货品图规则压缩 → uploadProductPic（bg.goods.image.upload.global）。
 * 平台用上传 base64 原图做尺寸/格式校验；前端须先压成合规图。不再复用 Shein transform-pic。
 */

import { temuPlatformAPI } from '@/utils/api';
import { resolveImageUrlToBlob, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { toManagedCanonicalUploadsUrl } from '@/utils/imageDisplayAccess';
import {
  TEMU_OUTER_PACKAGE_IMAGE_MAX_BYTES,
  TEMU_PRODUCT_IMAGE_MAX_BYTES,
  prepareTemuOuterPackageImage,
  prepareTemuProductImage,
} from '@/temu-platform/utils/temuProductImage';
import { toCanonicalUploadsPath } from '@/temu-platform/modules/products/components/publishImageUploadThumbSrc';
import {
  isGenericListingImageName,
  isOpaqueStorageBaseName,
} from '@/pages/ipe/workbench/utils/platformListingItemMedia';
import { findImageSpaceRecordByUrl } from '@/pages/ipe/workbench/utils/imageCardMetaActions';

/** 与 SHEIN 对齐：商品级并行；校验接口已做跨条目缓存，略提高吞吐 */
export const LISTING_PREPARE_CONVERT_CONCURRENCY = 6;

function asText(value) {
  return String(value || '').trim();
}

function fileNameFromUrl(url) {
  const raw = asText(url);
  if (!raw) return 'product.jpg';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]) || 'product.jpg';
  } catch {
    return (raw.split('/').pop() || '').split('?')[0] || 'product.jpg';
  }
}

/** 是否可用作展示名（排除 OSS 叶子名 / 系统占位名） */
function isReadableDisplayName(name) {
  const raw = asText(name);
  if (!raw) return false;
  const base = stripImageFileExtension(raw) || raw;
  return Boolean(base) && !isOpaqueStorageBaseName(base) && !isGenericListingImageName(base);
}

function pickReadableDisplayName(...candidates) {
  for (const raw of candidates) {
    const name = asText(raw);
    if (isReadableDisplayName(name)) return name;
  }
  return '';
}

function slotDisplayNameCandidates(slot) {
  if (!slot || typeof slot !== 'object') return [];
  return [
    slot.fileName,
    slot.originalName,
    slot.originalFileName,
    slot.displayName,
    slot.title,
    slot.name,
  ];
}

/**
 * 错误日志展示名：优先图片空间可读文件名，不用 OSS/img-{hash} 叶子名。
 * @param {string} url
 * @param {object|string|null} slotOrHint
 * @param {string} [fallback]
 */
async function resolveImageDisplayLabel(url, slotOrHint = '', fallback = '未命名') {
  const hints = (slotOrHint && typeof slotOrHint === 'object')
    ? slotDisplayNameCandidates(slotOrHint)
    : [slotOrHint];
  const picked = pickReadableDisplayName(...hints);
  if (picked) return picked;

  const source = asText(url);
  if (source && !source.startsWith('blob:') && !source.startsWith('data:')) {
    try {
      const record = await findImageSpaceRecordByUrl(source);
      const fromSpace = pickReadableDisplayName(
        record?.fileName,
        record?.originalName,
        record?.originalFileName,
        record?.displayName,
        record?.title,
        record?.name,
      );
      if (fromSpace) return fromSpace;
    } catch {
      /* 图片空间回表失败时用角色名兜底 */
    }
  }

  const role = asText(typeof slotOrHint === 'string' ? slotOrHint : '')
    || asText(fallback)
    || '未命名';
  if (role && !isOpaqueStorageBaseName(role)) return role;
  return asText(fallback) || '未命名';
}

/** 错误文案里附站内存储短路径（非展示文件名） */
function shortImageRef(url) {
  const u = asText(url);
  if (!u) return '';
  if (u.startsWith('data:')) return 'data:…';
  if (u.startsWith('blob:')) return 'blob:…';
  try {
    const path = new URL(u, 'https://local.invalid').pathname || u;
    const parts = path.split('/').filter(Boolean);
    return parts.slice(-2).join('/') || path;
  } catch {
    return u.length > 72 ? `…${u.slice(-72)}` : u;
  }
}

function withImageRef(message, source) {
  const msg = asText(message);
  const ref = shortImageRef(source);
  if (!msg) return ref ? i18n.t('workbench:ipeUtilError.storageRefOnly', { ref }) : '';
  if (!ref || msg.includes(ref)) return msg;
  return i18n.t('workbench:ipeUtilError.temuStorageParen', { message: msg, ref });
}

/**
 * 将站内读图失败码翻成可操作中文（EMPTY_SIGNED_URL / FETCH_403 等）。
 */
export function formatListingImageAccessError(err, { label = '', source = '' } = {}) {
  const raw = asText(err?.message || err);
  const head = asText(label) ? `图片「${asText(label)}」` : '图片';
  let detail = '';
  if (!raw || raw === 'EMPTY_IMAGE_URL') {
    detail = `${head}地址为空，无法下载站内原图`;
  } else if (raw === 'EMPTY_SIGNED_URL') {
    detail = `${head}无法签发读图地址（站内图可能已失效、无权访问或未落库）`;
  } else {
    const fetchMatch = raw.match(/^FETCH_(\d+)$/i);
    if (fetchMatch) {
      const code = fetchMatch[1];
      if (code === '403') {
        detail = `${head}下载被拒绝（HTTP 403：无权限或签名失效）`;
      } else if (code === '404') {
        detail = `${head}下载失败（HTTP 404：站内原图不存在或已删除）`;
      } else if (code === '401') {
        detail = `${head}下载失败（HTTP 401：未授权）`;
      } else {
        detail = `${head}下载失败（HTTP ${code}）`;
      }
    } else if (/failed to fetch|networkerror|network error|load failed|net::/i.test(raw)) {
      detail = `${head}下载失败：网络错误，请检查本机网络后重试`;
    } else if (raw.includes('下载') || raw.includes('签发') || raw.includes('站内')) {
      detail = raw;
    } else {
      detail = `${head}下载失败：${raw}`;
    }
  }
  return withImageRef(detail, source);
}

/** 预检与转图共用，避免同一张站内图下载两次 */
const readableBlobCache = new Map();

/**
 * 转图前校验：站内原图能否下载、是否非空。
 * @returns {Promise<{ blob: Blob, source: string, label: string }>}
 */
export async function assertListingImageReadable(url, nameHint = '') {
  const source = asText(url);
  const label = isReadableDisplayName(nameHint)
    ? asText(nameHint)
    : (await resolveImageDisplayLabel(source, nameHint, '未命名'));
  if (!source) {
    throw new Error(formatListingImageAccessError(new Error('EMPTY_IMAGE_URL'), { label, source }));
  }
  const cached = readableBlobCache.get(source);
  if (cached) {
    const hit = await cached;
    return { ...hit, label: isReadableDisplayName(nameHint) ? asText(nameHint) : (hit.label || label) };
  }
  const pending = (async () => {
    let blob;
    try {
      blob = await resolveImageUrlToBlob(source);
    } catch (err) {
      throw new Error(formatListingImageAccessError(err, { label, source }));
    }
    if (!blob || !(Number(blob.size) > 0)) {
      throw new Error(withImageRef(
        `图片「${label}」下载结果为空（0 字节），请检查原图是否损坏`,
        source,
      ));
    }
    const mime = String(blob.type || '').trim().toLowerCase();
    if (mime && !mime.startsWith('image/') && mime !== 'application/octet-stream') {
      throw new Error(withImageRef(
        `图片「${label}」下载内容不是图片（Content-Type: ${mime}）`,
        source,
      ));
    }
    return { blob, source, label };
  })();
  readableBlobCache.set(source, pending);
  try {
    return await pending;
  } catch (err) {
    readableBlobCache.delete(source);
    throw err;
  }
}

function summarizeTemuUploadResponse(res) {
  if (res == null) return i18n.t('workbench:ipeUtilError.temuEmptyResponse');
  const temuUrl = asText(res?.temuUrl);
  const url = asText(res?.url);
  const firstUrl = Array.isArray(res?.urls) ? asText(res.urls[0]) : '';
  const picked = temuUrl || url || firstUrl;
  if (!picked) {
    const keys = res && typeof res === 'object' ? Object.keys(res).slice(0, 8).join(',') : typeof res;
    return i18n.t('workbench:ipeUtilError.temuNoUrlFields', { keys: keys || i18n.t('workbench:ipeUtilError.temuNoFieldsFallback') });
  }
  const clipped = picked.length > 120 ? `${picked.slice(0, 120)}…` : picked;
  if (!isStrictTemuImageCdnUrl(picked)) {
    return i18n.t('workbench:ipeUtilError.temuNotCdnUrl', { url: clipped });
  }
  return clipped;
}

function pickTemuUrlFromUploadRes(res) {
  const direct = asText(res?.temuUrl || res?.url);
  if (direct) return direct;
  if (Array.isArray(res?.urls) && res.urls.length) {
    return asText(res.urls[0]);
  }
  return '';
}

/**
 * 只认 Temu 图片 CDN。卖家页、站内 /uploads、OSS 签名地址都不能当已转图。
 * 含空白或中文的地址平台会报「域名不合法或者url包含不合法字符串」。
 * 货品图上传现常返回 pfs.file.temu.com（含 product-material-private-tag），提交可用但浏览器预览常 403。
 */
export function isStrictTemuImageCdnUrl(url = '') {
  const u = asText(url);
  if (!u || /[\s<>"'\\\u4e00-\u9fff]/.test(u)) return false;
  let host = '';
  try {
    host = new URL(u).hostname.toLowerCase();
  } catch {
    return false;
  }
  return host === 'img.kwcdn.com'
    || host.endsWith('.kwcdn.com')
    || host.endsWith('.cdnfe.com')
    || host.endsWith('.pddpic.com')
    || host.endsWith('.pinduoduo.com')
    || host === 'file.temu.com'
    || host.endsWith('.file.temu.com')
    || host.includes('fancyalgo')
    || host.includes('kjpfs');
}

function looksLikeTemuHostedUrl(url) {
  return isStrictTemuImageCdnUrl(url);
}

export function looksLikeTemuImageTooLargeError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  if (!message) return false;
  return message.includes('3mb')
    || message.includes('3 mb')
    || message.includes('too large')
    || message.includes('size limit')
    || (message.includes('exceed') && message.includes('size'))
    || (message.includes('超过') && (message.includes('大') || message.includes('大小') || message.includes('限制')));
}

function packConverted(source, nameHint, res) {
  const temuUrl = pickTemuUrlFromUploadRes(res);
  if (!temuUrl) return null;
  const rawName = isReadableDisplayName(nameHint)
    ? asText(nameHint)
    : (pickReadableDisplayName(fileNameFromUrl(source)) || asText(nameHint) || 'product.jpg');
  const rawIpe = asText(res?.ipeUrl);
  const rawPublic = asText(res?.publicUrl);
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || toManagedCanonicalUploadsUrl(source)
    || rawIpe
    || rawPublic
    || source;
  return {
    name: stripImageFileExtension(rawName) || rawName,
    temuUrl,
    ipeUrl: durable,
    publicUrl: durable,
    previewUrl: durable,
    url: temuUrl,
  };
}

/**
 * @param {{ shopId: string|number, url: string, imageType?: number, nameHint?: string, forPackage?: boolean }} params
 * @returns {Promise<{ temuUrl: string, ipeUrl: string, publicUrl: string, previewUrl: string, name: string }|null>}
 */
export async function convertListingImageToTemu({
  shopId,
  url,
  imageType = 1,
  nameHint = '',
  forPackage = false,
} = {}) {
  const source = asText(url);
  const label = await resolveImageDisplayLabel(source, nameHint, '未命名');
  if (!shopId || !source) return null;
  // 外包装必须用 imageBizType=1 重新上传；已是货品图 CDN 也不能直接复用
  if (!forPackage && looksLikeTemuHostedUrl(source)) {
    return packConverted(source, label, { temuUrl: source, ipeUrl: source, publicUrl: source });
  }

  // 转图前先校验站内原图能否下载（失败直接给出具体原因，不进入 Temu 上传）
  const { blob } = await assertListingImageReadable(source, label);
  const rawName = isReadableDisplayName(nameHint) ? asText(nameHint) : label;
  // 空间里常见 webp/heic：上传前统一落到 JPG/PNG，避免 Temu 格式校验直接拒绝
  const blobMime = String(blob.type || '').trim().toLowerCase();
  const preferPng = blobMime === 'image/png' || /\.png$/i.test(rawName);
  const safeBase = (rawName.includes('.') ? rawName.replace(/\.[^.]+$/, '') : (rawName || 'product'))
    || 'product';
  const safeName = `${safeBase}.${preferPng ? 'png' : 'jpg'}`;
  const safeType = preferPng ? 'image/png' : 'image/jpeg';
  const file = new File(
    [blob],
    safeName,
    { type: safeType, lastModified: Date.now() },
  );
  // 外包装：官方未要求 3:4，只做格式/最小边/体积；货品图仍按主图规则压图
  let prepared;
  try {
    prepared = forPackage
      ? await prepareTemuOuterPackageImage(file)
      : await prepareTemuProductImage(file, { requireAspect: false });
  } catch (prepErr) {
    throw new Error(withImageRef(
      `图片「${label}」处理失败：${asText(prepErr?.message) || '压缩/裁剪失败'}`,
      source,
    ));
  }
  const maxBytes = forPackage ? TEMU_OUTER_PACKAGE_IMAGE_MAX_BYTES : TEMU_PRODUCT_IMAGE_MAX_BYTES;
  if (prepared.size > maxBytes) {
    throw new Error(withImageRef(
      forPackage ? `外包装图「${label}」超过 3MB，请压缩后再试` : `图片「${label}」超过 2MB，请压缩后再试`,
      source,
    ));
  }
  let res;
  try {
    res = await temuPlatformAPI.uploadProductPic(shopId, prepared, {
      imageBizType: forPackage ? 1 : 0,
      // imageType 仅前端语义（主图/细节），Temu 货品图统一 imageBizType=0
      imageType,
      // 刊登/空间选图：原图已在图片空间，勿重复登记
      fromImageSpace: true,
    });
  } catch (uploadErr) {
    throw new Error(withImageRef(
      `图片「${label}」上传 Temu 失败：${asText(uploadErr?.message) || '平台接口报错'}`,
      source,
    ));
  }
  const packed = packConverted(source, nameHint, {
    ...res,
    temuUrl: pickTemuUrlFromUploadRes(res),
    // 优先保留空间原图地址
    ipeUrl: res?.ipeUrl || toCanonicalUploadsPath(source) || toManagedCanonicalUploadsUrl(source) || source,
    publicUrl: res?.publicUrl || toCanonicalUploadsPath(source) || source,
  });
  if (!packed?.temuUrl || !isStrictTemuImageCdnUrl(packed.temuUrl)) {
    throw new Error(withImageRef(
      `图片「${label}」未能转成 Temu 图址：${summarizeTemuUploadResponse(res)}`,
      source,
    ));
  }
  return packed;
}

function slotSourceUrl(slot) {
  if (!slot || typeof slot !== 'object') return '';
  // temuUrl 若误写了站内/OSS 地址，也必须参与转图，不能直接丢弃
  const candidates = [
    slot.ipeUrl,
    slot.publicUrl,
    slot.previewUrl,
    slot.url,
    slot.originalUrl,
    slot.temuUrl,
  ];
  return candidates.map((item) => asText(item)).find((item) => (
    item
    && !item.startsWith('blob:')
    && !item.startsWith('data:')
    && !isStrictTemuImageCdnUrl(item)
  )) || '';
}

async function convertFormImageSlot(slot, { shopId, forPackage = false, imageType = 1 } = {}) {
  if (!slot || typeof slot !== 'object') return slot;
  const temuUrl = asText(slot.temuUrl);
  if (!forPackage && isStrictTemuImageCdnUrl(temuUrl)) return slot;
  const source = slotSourceUrl(slot);
  if (!source) {
    // 无可用原图：清掉伪 temuUrl，避免把非 CDN 地址提交给平台
    if (temuUrl && !isStrictTemuImageCdnUrl(temuUrl)) {
      const next = { ...slot };
      delete next.temuUrl;
      return next;
    }
    return slot;
  }
  const label = await resolveImageDisplayLabel(source, slot, '未命名');
  try {
    const converted = await convertListingImageToTemu({
      shopId,
      url: source,
      imageType,
      nameHint: label,
      forPackage,
    });
    if (!converted?.temuUrl || !isStrictTemuImageCdnUrl(converted.temuUrl)) {
      throw new Error(withImageRef(
        `图片「${label}」未能转成 Temu 图址：平台未返回合法 CDN`,
        source,
      ));
    }
    const keepName = isReadableDisplayName(slot.name) ? slot.name : (converted.name || label);
    return {
      ...slot,
      ...converted,
      id: slot.id,
      name: keepName,
    };
  } catch (error) {
    const message = asText(error?.message);
    if (message) throw error;
    throw new Error(withImageRef(
      `图片「${label}」未能转成 Temu 图址`,
      source,
    ));
  }
}

function pushReadableCandidate(list, seen, url, nameHint, slot = null) {
  const source = asText(url);
  if (!source || source.startsWith('blob:') || source.startsWith('data:')) return;
  if (isStrictTemuImageCdnUrl(source)) return;
  if (seen.has(source)) return;
  seen.add(source);
  list.push({
    url: source,
    nameHint: asText(nameHint) || '未命名',
    slot: slot && typeof slot === 'object' ? slot : null,
  });
}

/** 收集表单中待转图的站内原图（去重），供转图前批量可读校验 */
function collectFormImageReadableCandidates(form) {
  const list = [];
  const seen = new Set();
  const skcList = Array.isArray(form?.skcList) ? form.skcList : [];
  skcList.forEach((skc) => {
    if (!skc || typeof skc !== 'object') return;
    ['mainImage', 'squareImage', 'colorBlockImage'].forEach((field) => {
      const slot = skc[field];
      if (!slot || typeof slot !== 'object') return;
      pushReadableCandidate(list, seen, slotSourceUrl(slot), field, slot);
    });
    (Array.isArray(skc.detailImages) ? skc.detailImages : []).forEach((slot, idx) => {
      if (!slot || typeof slot !== 'object') return;
      pushReadableCandidate(list, seen, slotSourceUrl(slot), `细节图${idx + 1}`, slot);
    });
    if (skc.showroomSlots && typeof skc.showroomSlots === 'object') {
      Object.entries(skc.showroomSlots).forEach(([key, slot]) => {
        if (!slot || typeof slot !== 'object') return;
        pushReadableCandidate(list, seen, slotSourceUrl(slot), key, slot);
      });
    }
    (Array.isArray(skc.skuRows) ? skc.skuRows : []).forEach((row) => {
      const slot = row?.skuImage;
      if (!slot || typeof slot !== 'object') return;
      pushReadableCandidate(list, seen, slotSourceUrl(slot), 'SKU图', slot);
    });
  });
  if (form?.materialImage && typeof form.materialImage === 'object') {
    pushReadableCandidate(
      list,
      seen,
      slotSourceUrl(form.materialImage),
      '素材图',
      form.materialImage,
    );
  }
  (Array.isArray(form?.outerPackageImages) ? form.outerPackageImages : []).forEach((slot, idx) => {
    if (!slot || typeof slot !== 'object') return;
    pushReadableCandidate(list, seen, slotSourceUrl(slot), `外包装${idx + 1}`, slot);
  });
  (Array.isArray(form?.goodsModels) ? form.goodsModels : []).forEach((model) => {
    if (!model || typeof model !== 'object') return;
    const current = asText(model.modelProfileUrl);
    if (isStrictTemuImageCdnUrl(current)) return;
    const source = asText(model._portraitIpe) || current;
    pushReadableCandidate(list, seen, source, model.modelName || '模特头像', model);
  });
  return list;
}

/**
 * 转图前批量校验站内原图能否下载；任一张失败即抛出明确原因。
 */
export async function preflightTemuFormImagesReadable(form) {
  const candidates = collectFormImageReadableCandidates(form);
  if (!candidates.length) return;
  await mapPool(candidates, async ({ url, nameHint, slot }) => {
    const label = await resolveImageDisplayLabel(url, slot || nameHint, nameHint);
    await assertListingImageReadable(url, label);
  });
}

async function mapPool(items, mapper) {
  const list = Array.isArray(items) ? items : [];
  const out = new Array(list.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < list.length) {
      const index = cursor;
      cursor += 1;
      out[index] = await mapper(list[index], index);
    }
  };
  const workers = Math.min(LISTING_PREPARE_CONVERT_CONCURRENCY, list.length);
  if (workers > 0) {
    await Promise.all(Array.from({ length: workers }, () => worker()));
  }
  return out;
}

/**
 * 发布前把官方模板带入的站内图（/uploads、OSS）转成 Temu 图片 CDN。
 * 已是合规 CDN 的槽位不动；转不成的地址不再提交，避免域名不合法。
 */
export async function ensureTemuFormImagesHosted(form, { shopId } = {}) {
  if (!form || typeof form !== 'object' || !shopId) return form;
  const jobs = [];
  const skcList = (Array.isArray(form.skcList) ? form.skcList : []).map((skc) => (
    skc && typeof skc === 'object' ? { ...skc } : skc
  ));
  const next = {
    ...form,
    skcList,
  };

  // 先批量校验站内原图能否下载，再调 Temu 转图（失败原因：签发失败 / 403 / 404 等）
  await preflightTemuFormImagesReadable(next);

  skcList.forEach((skc, skcIndex) => {
    if (!skc || typeof skc !== 'object') return;
    ['mainImage', 'squareImage', 'colorBlockImage'].forEach((field) => {
      const slot = skc[field];
      if (!slot) return;
      jobs.push(async () => {
        skcList[skcIndex][field] = await convertFormImageSlot(slot, {
          shopId,
          imageType: field === 'mainImage' ? 1 : 2,
        });
      });
    });
    if (Array.isArray(skc.detailImages) && skc.detailImages.length) {
      jobs.push(async () => {
        skcList[skcIndex].detailImages = await mapPool(skc.detailImages, (slot) => (
          convertFormImageSlot(slot, { shopId, imageType: 2 })
        ));
      });
    }
    if (skc.showroomSlots && typeof skc.showroomSlots === 'object') {
      jobs.push(async () => {
        const entries = Object.entries(skc.showroomSlots);
        const converted = await mapPool(entries, async ([key, slot]) => [
          key,
          await convertFormImageSlot(slot, { shopId, imageType: 2 }),
        ]);
        skcList[skcIndex].showroomSlots = Object.fromEntries(converted);
      });
    }
    if (Array.isArray(skc.skuRows) && skc.skuRows.some((row) => row?.skuImage)) {
      jobs.push(async () => {
        skcList[skcIndex].skuRows = await mapPool(skc.skuRows, async (row) => {
          if (!row?.skuImage) return row;
          return {
            ...row,
            skuImage: await convertFormImageSlot(row.skuImage, { shopId, imageType: 2 }),
          };
        });
      });
    }
  });

  if (next.materialImage) {
    jobs.push(async () => {
      next.materialImage = await convertFormImageSlot(next.materialImage, { shopId, imageType: 1 });
      const temuUrl = asText(next.materialImage?.temuUrl);
      next.materialImgUrl = isStrictTemuImageCdnUrl(temuUrl) ? temuUrl : '';
    });
  } else if (!isStrictTemuImageCdnUrl(next.materialImgUrl)) {
    next.materialImgUrl = '';
  }

  if (Array.isArray(next.outerPackageImages) && next.outerPackageImages.length) {
    jobs.push(async () => {
      next.outerPackageImages = await mapPool(next.outerPackageImages, (slot) => (
        convertFormImageSlot(slot, { shopId, forPackage: true, imageType: 1 })
      ));
      next.productOuterPackageImageReqs = next.outerPackageImages
        .map((img) => asText(img?.temuUrl))
        .filter((url) => isStrictTemuImageCdnUrl(url))
        .map((imageUrl) => ({ imageUrl }));
    });
  } else if (Array.isArray(next.productOuterPackageImageReqs)) {
    next.productOuterPackageImageReqs = next.productOuterPackageImageReqs
      .map((row) => asText(typeof row === 'string' ? row : row?.imageUrl || row?.temuUrl))
      .filter((url) => isStrictTemuImageCdnUrl(url))
      .map((imageUrl) => ({ imageUrl }));
  }

  if (Array.isArray(next.goodsModels) && next.goodsModels.length) {
    jobs.push(async () => {
      next.goodsModels = await mapPool(next.goodsModels, async (model) => {
        if (!model || typeof model !== 'object') return model;
        const current = asText(model.modelProfileUrl);
        if (isStrictTemuImageCdnUrl(current)) return model;
        const source = asText(model._portraitIpe) || current;
        if (!source || source.startsWith('blob:') || source.startsWith('data:')) {
          const cleared = { ...model };
          delete cleared.modelProfileUrl;
          return cleared;
        }
        const converted = await convertListingImageToTemu({
          shopId,
          url: source,
          imageType: 1,
          nameHint: model.modelName || 'model',
        });
        if (!converted?.temuUrl || !isStrictTemuImageCdnUrl(converted.temuUrl)) {
          throw new Error(withImageRef(
            `模特头像「${asText(model.modelName) || '未命名'}」未能转成 Temu 图址：平台未返回合法 CDN`,
            source,
          ));
        }
        return { ...model, modelProfileUrl: converted.temuUrl };
      });
    });
  }

  await mapPool(jobs, (job) => job());
  return next;
}
